/**
 * Applying the patches an edit is broadcast as (see api/services/state_delta.py).
 *
 * Copy-on-write: only the objects on a changed path are copied; everything else keeps
 * its identity, so the memos keyed on the state's parts (grid data, canvases) rebuild
 * for what changed and nothing more. Cost: the size of the change.
 *
 * Patch format:
 *   {v: value}                       replace with value
 *   {o: {key: patch}, d: [key, ...]} object: patch these keys, delete those
 *   {i: {index: patch}}              array: patch these items
 *   {s: [start, deleteCount, items]} array: splice
 */
export type StatePatch =
  | { v: unknown }
  | { o: Record<string, StatePatch>; d?: string[] }
  | { i: Record<string, StatePatch> }
  | { s: [number, number, unknown[]] };

export function applyPatch<T>(value: T, patch: StatePatch | null | undefined): T {
  if (!patch) return value;
  if ('v' in patch) return patch.v as T;
  if ('o' in patch) {
    const out: Record<string, unknown> = { ...(value as Record<string, unknown> | null ?? {}) };
    for (const [key, sub] of Object.entries(patch.o)) out[key] = applyPatch(out[key], sub);
    for (const key of patch.d ?? []) delete out[key];
    return out as T;
  }
  if ('i' in patch) {
    const out = [...(value as unknown[])];
    for (const [index, sub] of Object.entries(patch.i)) out[Number(index)] = applyPatch(out[Number(index)], sub);
    return out as T;
  }
  const [start, count, items] = patch.s;
  const out = [...(value as unknown[])];
  out.splice(start, count, ...items);
  return out as T;
}

/** Whether `patch` replaces the whole state — it applies whatever revision is held. */
export function isWholeState(patch: StatePatch | null | undefined): boolean {
  return !!patch && 'v' in patch;
}
